import { loadBaseCSS } from '../shared/button-utils.js';
import { attachInternalsSafe, generateControlId, isAttrTruthy, reflectBoolean, upgradeProperty } from '../shared/form-control.js';
import { redirectValidationFocus } from '../shared/focus-utils.js';

export type QdsUploadStatus = 'queued' | 'uploading' | 'completed' | 'error' | 'cancelled';
export interface QdsUploadTask {
  readonly id: string;
  readonly file: File;
  readonly status: QdsUploadStatus | 'rejected';
  readonly progress: number | null;
  readonly error: string;
  readonly rejection: string;
  readonly result: unknown;
}
export interface QdsUploadContext {
  readonly signal: AbortSignal;
  readonly taskId: string;
  readonly onProgress: (loaded: number, total?: number) => void;
}
export type QdsUploadHandler = (file: File, context: QdsUploadContext) => Promise<unknown>;
interface UploadRecord {
  id: string; file: File; status: QdsUploadStatus; progress: number | null;
  error: string; rejection: string; result: unknown; requested: boolean; controller?: AbortController;
}
interface UploadValidity { valid: boolean; valueMissing: boolean; badInput: boolean; customError: boolean; }

/**
 * File selection and cancellable, application-owned transfer tasks.
 * Native invalid reporting returns focus to the visible chooser without hiding its caption.
 * @element qds-file-upload
 * @attr {string} label - Visible group label; default Attachments.
 * @attr {string} aria-label - Explicit group name.
 * @attr {string} aria-labelledby - External label IDs, copied to the same-root group name.
 * @attr {string} hint - Selection instructions.
 * @attr {string} name - Repeated native File submission key.
 * @attr {string} form - External native form ID.
 * @attr {string} accept - Comma-separated extension, MIME type or MIME wildcard hints.
 * @attr {boolean} multiple - Append multiple files; default single selection.
 * @attr {number} max-size - Maximum bytes per file; zero means unlimited.
 * @attr {number} max-files - Maximum selected count; zero means unlimited.
 * @attr {number} concurrency - Positive parallel transfer limit, clamped to 1–6; invalid values default to 2.
 * @attr {boolean} auto - Start accepted user selections when a handler is supplied.
 * @attr {boolean} dropzone - Enable drop selection; default true, use false to hide it.
 * @attr {boolean} disabled - Disable actions and omit native submission.
 * @attr {boolean} readonly - Keep selected files submitted while preventing changes/transfers.
 * @attr {boolean} required - Require a selected file, independently of transfer completion.
 * @attr {boolean} invalid - Author invalid state.
 * @attr {string} error-text - Author validation message.
 * @property {File[]} files - Cloned selected File array; silent owner writes preserve reference identity.
 * @property {ReadonlyArray<{id:string;file:File;status:'queued'|'uploading'|'completed'|'error'|'cancelled'|'rejected';progress:number|null;error:string;rejection:string;result:unknown}>} tasks - Read-only task snapshots; no storage is implied by completion.
 * @property {(file:File,context:{signal:AbortSignal;taskId:string;onProgress:(loaded:number,total?:number)=>void})=>Promise<unknown>} uploadHandler - Application transport; no endpoint/authentication/storage policy is built in.
 * @csspart group - Named composite surface.
 * @csspart chooser - Visible native picker action and public focus target.
 * @csspart dropzone - Optional drag instructions, with picker alternative.
 * @csspart files - Selected-file list.
 * @csspart file - Stable task row.
 * @csspart progress - Named native transfer progress.
 * @csspart status - Aggregate live feedback.
 * @csspart hint - Helper text.
 * @csspart error - Native constraint message.
 * @fires {{component:HTMLElement;files:File[];value:File[]}} input - Explicit picker/drop/remove/clear selection change, once.
 * @fires {{component:HTMLElement;files:File[];value:File[]}} change - Same selection detail; owner writes/reset/restore are silent.
 * @fires {{component:HTMLElement;task:{id:string;file:File;status:string;progress:number|null;error:string;rejection:string;result:unknown}}} upload-start - A fresh transport attempt.
 * @fires {{component:HTMLElement;task:{id:string;file:File;status:string;progress:number|null;error:string;rejection:string;result:unknown}}} upload-progress - A finite, clamped progress update.
 * @fires {{component:HTMLElement;task:{id:string;file:File;status:string;progress:number|null;error:string;rejection:string;result:unknown}}} upload-complete - A current attempt completed exactly once.
 * @fires {{component:HTMLElement;task:{id:string;file:File;status:string;progress:number|null;error:string;rejection:string;result:unknown}}} upload-error - A current attempt failed; retry is explicit.
 * @fires {{component:HTMLElement;task:{id:string;file:File;status:string;progress:number|null;error:string;rejection:string;result:unknown}}} upload-cancel - Requested or running work was cancelled.
 * @fires {{component:HTMLElement;valid:boolean;validity:{valid:boolean;valueMissing:boolean;badInput:boolean;customError:boolean};message:string}} qds-validity-change - Native validity transition.
 */
export class QdsFileUpload extends HTMLElement {
  static formAssociated = true;
  static qdsFieldConsumer = true;
  static get observedAttributes(): string[] {
    return ['label', 'aria-label', 'aria-labelledby', 'hint', 'name', 'form', 'accept', 'multiple', 'max-size', 'max-files', 'concurrency', 'auto', 'dropzone', 'disabled', 'readonly', 'required', 'invalid', 'error-text'];
  }
  private _id = generateControlId('upload');
  private _serial = 0;
  private _records: UploadRecord[] = [];
  private _rows = new Map<string, HTMLLIElement>();
  private _internals?: ElementInternals;
  private _handler?: QdsUploadHandler;
  private _fieldsetDisabled = false;
  private _customError = '';
  private _validity: UploadValidity = { valid: true, valueMissing: false, badInput: false, customError: false };
  private _message = '';
  private _showError = false;
  private _pumping = false;
  private _waiters: Array<{ ids: string[]; resolve: () => void }> = [];
  private _chooser!: HTMLButtonElement;
  private _onValidationFocus = redirectValidationFocus.bind(this);
  private _picker!: HTMLInputElement;
  private _list!: HTMLOListElement;
  private _group!: HTMLDivElement;
  constructor() {
    super(); this.attachShadow({ mode: 'open' }); loadBaseCSS();
    const internals = attachInternalsSafe(this);
    if (typeof internals?.setFormValue === 'function' && typeof internals?.setValidity === 'function') this._internals = internals;
  }
  get label(): string { return this.getAttribute('label') || 'Attachments'; }
  set label(value: string) { this.setAttribute('label', value ?? ''); }
  get hint(): string { return this.getAttribute('hint') || ''; }
  set hint(value: string) { this.setAttribute('hint', value ?? ''); }
  get name(): string { return this.getAttribute('name') || ''; }
  set name(value: string) { this.setAttribute('name', value ?? ''); }
  get accept(): string { return this.getAttribute('accept') || ''; }
  set accept(value: string) { this.setAttribute('accept', value ?? ''); }
  get errorText(): string { return this.getAttribute('error-text') || ''; }
  set errorText(value: string) { this.setAttribute('error-text', value ?? ''); }
  get multiple(): boolean { return isAttrTruthy(this.getAttribute('multiple')); }
  set multiple(value: boolean) { reflectBoolean(this, 'multiple', value); }
  get disabled(): boolean { return this._fieldsetDisabled || isAttrTruthy(this.getAttribute('disabled')); }
  set disabled(value: boolean) { reflectBoolean(this, 'disabled', value); }
  get readonly(): boolean { return isAttrTruthy(this.getAttribute('readonly')); }
  set readonly(value: boolean) { reflectBoolean(this, 'readonly', value); }
  get required(): boolean { return isAttrTruthy(this.getAttribute('required')); }
  set required(value: boolean) { reflectBoolean(this, 'required', value); }
  get invalid(): boolean { return isAttrTruthy(this.getAttribute('invalid')); }
  set invalid(value: boolean) { reflectBoolean(this, 'invalid', value); }
  get auto(): boolean { return isAttrTruthy(this.getAttribute('auto')); }
  set auto(value: boolean) { reflectBoolean(this, 'auto', value); }
  get dropzone(): boolean { return !this.hasAttribute('dropzone') || isAttrTruthy(this.getAttribute('dropzone')); }
  set dropzone(value: boolean) { this.setAttribute('dropzone', String(value)); }
  get maxSize(): number { return this._limit('max-size'); }
  set maxSize(value: number) { this.setAttribute('max-size', String(value)); }
  get maxFiles(): number { return Math.floor(this._limit('max-files')); }
  set maxFiles(value: number) { this.setAttribute('max-files', String(value)); }
  get concurrency(): number { return Math.min(6, Math.max(1, Math.floor(this._limit('concurrency') || 2))); }
  set concurrency(value: number) { this.setAttribute('concurrency', String(value)); }
  get files(): File[] { return this._records.map(record => record.file); }
  set files(value: File[]) { this._replace(Array.isArray(value) ? value : [], false); }
  get tasks(): ReadonlyArray<QdsUploadTask> { return Object.freeze(this._records.map(record => this._snapshot(record))); }
  get uploadHandler(): QdsUploadHandler | undefined { return this._handler; }
  set uploadHandler(value: QdsUploadHandler | undefined) {
    const handler = typeof value === 'function' ? value : undefined;
    if (handler === this._handler) return;
    this._handler = handler; this._cancelRecords(this._records); this._sync();
  }
  get form(): HTMLFormElement | null {
    const formId = this.getAttribute('form');
    const candidate = formId ? this.ownerDocument.getElementById(formId) : this.closest('form');
    return this._internals?.form || (candidate?.tagName === 'FORM' ? candidate as HTMLFormElement : null);
  }
  get validity(): UploadValidity { return { ...this._validity }; }
  get validationMessage(): string { return this._message; }
  get willValidate(): boolean { return !this.disabled && !this.readonly; }
  setCustomValidity(message: string): void { this._customError = String(message); this._sync(); }
  checkValidity(): boolean {
    this._sync(); if (this._internals) return this._internals.checkValidity();
    if (!this._validity.valid) this.dispatchEvent(new Event('invalid', { cancelable: true }));
    return this._validity.valid;
  }
  reportValidity(): boolean {
    this._showError = true; this._sync();
    if (this._internals) return this._internals.reportValidity();
    const valid = this.checkValidity(); if (!valid) this.focus(); return valid;
  }
  override focus(options?: FocusOptions): void { if (!this.disabled) this._chooser?.focus(options); }
  choose(): void { if (this._mutable && this._picker) this._picker.click(); }
  /** Remove a task by stable ID. Explicit selection actions emit input/change. */
  removeFile(id: string): boolean {
    if (!this._mutable) return false;
    const record = this._records.find(item => item.id === id); if (!record) return false;
    this._replace(this._records.filter(item => item !== record).map(item => item.file), true); return true;
  }
  clear(): void { if (this._mutable) this._replace([], true); }
  /** Resolve when the requested attempts settle, including cancellation/removal. */
  upload(id?: string): Promise<void> {
    return this._enqueue(this._records.filter(record => !id || record.id === id));
  }
  private _enqueue(candidates: UploadRecord[]): Promise<void> {
    if (!this._mutable || !this._handler) return Promise.resolve();
    const records = candidates.filter(record => this._records.includes(record) && !record.rejection && record.status !== 'completed');
    for (const record of records) if (!record.controller) { record.requested = true; record.status = 'queued'; record.error = ''; }
    const promise = new Promise<void>(resolve => this._waiters.push({ ids: records.map(record => record.id), resolve }));
    this._sync(); this._pump(); return promise;
  }
  retry(id: string): Promise<void> {
    const record = this._records.find(item => item.id === id);
    return record && (record.status === 'error' || record.status === 'cancelled') ? this.upload(id) : Promise.resolve();
  }
  cancel(id?: string): void {
    this._cancelRecords(this._records.filter(record => !id || record.id === id)); this._sync(); this._pump();
  }
  connectedCallback(): void {
    for (const name of ['label', 'hint', 'name', 'accept', 'errorText', 'multiple', 'disabled', 'readonly', 'required', 'invalid', 'auto', 'dropzone', 'maxSize', 'maxFiles', 'concurrency', 'files', 'uploadHandler']) upgradeProperty(this, name);
    if (!this._chooser) this._render();
    this._picker.addEventListener('focus', this._onValidationFocus);
    for (const type of ['click', 'input', 'change', 'dragover', 'dragleave', 'drop', 'focusout']) this.shadowRoot!.addEventListener(type, this._onEvent);
    this.addEventListener('invalid', this._onInvalid); this._sync();
  }
  disconnectedCallback(): void {
    this._picker?.removeEventListener('focus', this._onValidationFocus);
    for (const type of ['click', 'input', 'change', 'dragover', 'dragleave', 'drop', 'focusout']) this.shadowRoot!.removeEventListener(type, this._onEvent);
    this.removeEventListener('invalid', this._onInvalid); this._cancelRecords(this._records); this._sync();
  }
  attributeChangedCallback(): void {
    if (this.disabled || this.readonly) this._cancelRecords(this._records);
    this._sync(); this._pump();
  }
  formDisabledCallback(disabled: boolean): void {
    this._fieldsetDisabled = disabled; if (disabled) this._cancelRecords(this._records); this._sync();
  }
  formResetCallback(): void { this._showError = false; this._replace([], false); }
  formStateRestoreCallback(state: string | File | FormData): void {
    const view = this.ownerDocument.defaultView!;
    if (state instanceof view.File) this._replace([state], false, true);
    else if (state instanceof view.FormData) this._replace([...state.values()].filter(value => value instanceof view.File) as File[], false, true);
  }
  private get _mutable(): boolean { return this.isConnected && !this.disabled && !this.readonly; }
  private _limit(name: string): number { const value = Number(this.getAttribute(name)); return Number.isFinite(value) && value > 0 ? value : 0; }
  private _snapshot(record: UploadRecord): QdsUploadTask {
    return Object.freeze({ id: record.id, file: record.file, status: record.rejection ? 'rejected' : record.status, progress: record.progress, error: record.error, rejection: record.rejection, result: record.result });
  }
  private _emit(type: string, detail: object): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail: { component: this, ...detail } }));
  }
  private _replace(files: File[], user: boolean, restore = false): void {
    const FileType = this.ownerDocument.defaultView!.File;
    const selected = [...new Set(files.filter(file => file instanceof FileType))];
    if (!restore && selected.length === this._records.length && selected.every((file, index) => file === this._records[index].file)) return;
    const previous = this._records;
    this._records = selected.map(file => (!restore && previous.find(record => record.file === file)) || {
      id: `${this._id}-${++this._serial}`, file, status: 'queued', progress: null, error: '', rejection: '', result: undefined, requested: false,
    });
    const added = user && this.auto ? this._records.filter(record => !previous.includes(record)) : [];
    this._cancelRecords(previous.filter(record => !this._records.includes(record)));
    this._sync();
    if (user) {
      this._showError = true; this._sync();
      for (const type of ['input', 'change']) this._emit(type, { files: this.files, value: this.files });
      if (this.auto) void this._enqueue(added);
    }
    this._settle();
  }
  private _rejection(file: File, index: number): string {
    const count = this.multiple ? this.maxFiles : 1;
    if (count && index >= count) return `Select no more than ${count} file${count === 1 ? '' : 's'}.`;
    if (this.maxSize && file.size > this.maxSize) return `File exceeds ${this.maxSize} bytes.`;
    const hints = this.accept.toLowerCase().split(',').map(hint => hint.trim()).filter(hint => /^\.[^\s,]+$|^[\w!#$&^.+-]+\/(?:[\w!#$&^.+-]+|\*)$/.test(hint));
    if (!hints.length || hints.some(hint => hint.startsWith('.') ? file.name.toLowerCase().endsWith(hint) : hint.endsWith('/*') ? file.type.toLowerCase().startsWith(hint.slice(0, -1)) : file.type.toLowerCase() === hint)) return '';
    return `File type is not accepted (${this.accept}).`;
  }
  private _cancelRecords(records: UploadRecord[]): void {
    for (const record of records) {
      const pending = Boolean(record.controller || record.requested);
      const controller = record.controller; record.controller = undefined; record.requested = false;
      if (pending) { record.status = 'cancelled'; record.error = ''; controller?.abort(); this._emit('upload-cancel', { task: this._snapshot(record) }); }
    }
    this._settle();
  }
  private _current(record: UploadRecord, controller: AbortController): boolean {
    return this._mutable && record.controller === controller && !controller.signal.aborted && this._records.includes(record);
  }
  private _pump(): void {
    if (this._pumping || !this._mutable || !this._handler) { this._settle(); return; }
    this._pumping = true;
    try {
      for (const record of this._records) {
        if (!this._mutable || !this._handler) break;
        if (this._records.filter(item => item.controller).length >= this.concurrency) break;
        if (this._records.includes(record) && record.requested && !record.rejection && !record.controller) this._start(record);
      }
    } finally { this._pumping = false; this._settle(); }
  }
  private _start(record: UploadRecord): void {
    const controller = new AbortController(), handler = this._handler!;
    record.controller = controller; record.requested = false; record.status = 'uploading'; record.progress = null; record.error = ''; record.result = undefined;
    this._sync(); this._emit('upload-start', { task: this._snapshot(record) });
    void Promise.resolve().then(() => {
      if (!this._current(record, controller)) return;
      return handler(record.file, { signal: controller.signal, taskId: record.id, onProgress: (loaded, total = record.file.size) => {
        if (!this._current(record, controller) || !Number.isFinite(loaded) || !Number.isFinite(total) || total <= 0) return;
        const progress = Math.max(0, Math.min(100, loaded / total * 100));
        if (record.progress === progress) return;
        record.progress = progress; this._syncRows(); this._emit('upload-progress', { task: this._snapshot(record) });
      } });
    }).then(result => {
      if (!this._current(record, controller)) return;
      record.controller = undefined; record.status = 'completed'; record.progress = 100; record.result = result;
      this._sync(); this._emit('upload-complete', { task: this._snapshot(record) }); this._pump();
    }, error => {
      if (!this._current(record, controller)) return;
      record.controller = undefined; record.status = 'error'; record.error = error instanceof Error ? error.message : String(error || 'Upload failed.');
      this._sync(); this._emit('upload-error', { task: this._snapshot(record) }); this._pump();
    });
  }
  private _settle(): void {
    const pending = this._waiters; this._waiters = [];
    for (const waiter of pending) {
      if (this._records.some(record => waiter.ids.includes(record.id) && (record.requested || record.controller))) this._waiters.push(waiter);
      else waiter.resolve();
    }
  }
  private _validate(): void {
    const valueMissing = this.willValidate && this.required && !this._records.length;
    const badInput = this.willValidate && this._records.some(record => Boolean(record.rejection));
    const customError = this.willValidate && Boolean(this._customError || this.invalid);
    const valid = !valueMissing && !badInput && !customError;
    const message = valid ? '' : this._customError || this.errorText || (valueMissing ? 'Choose a file.' : this._records.find(record => record.rejection)?.rejection) || 'Please check this field.';
    const changed = JSON.stringify(this._validity) !== JSON.stringify({ valid, valueMissing, badInput, customError }) || this._message !== message;
    this._validity = { valid, valueMissing, badInput, customError }; this._message = message;
    this._internals?.setValidity(valid ? {} : { valueMissing, badInput, customError }, message, this._picker);
    if (changed) this._emit('qds-validity-change', { valid, validity: this.validity, message });
  }
  private _sync(): void {
    if (!this._chooser) return;
    this._records.forEach((record, index) => { record.rejection = this._rejection(record.file, index); });
    this._cancelRecords(this._records.filter(record => Boolean(record.rejection)));
    const label = this.shadowRoot!.querySelector<HTMLElement>('[part=label]')!;
    label.textContent = `${this.label}${this.required ? ' *' : ''}`;
    const root = this.getRootNode() as Document | ShadowRoot;
    const ids = this.getAttribute('aria-labelledby') || '';
    const external = ids.split(/\s+/).map(id => (typeof root.getElementById === 'function' ? root : this.ownerDocument).getElementById(id)?.textContent || '').join(' ').trim();
    const name = this.getAttribute('aria-label') || external || this.label;
    this._group.setAttribute('aria-label', name); this._group.setAttribute('aria-disabled', String(this.disabled));
    this._chooser.textContent = this.multiple ? 'Choose files' : 'Choose file'; this._chooser.setAttribute('aria-label', `${this._chooser.textContent} for ${name}`);
    this._chooser.disabled = this.disabled || this.readonly;
    this._picker.disabled = this.disabled || this.readonly; this._picker.multiple = this.multiple; this._picker.accept = this.accept;
    this.shadowRoot!.querySelector<HTMLElement>('[part=dropzone]')!.hidden = !this.dropzone;
    const hint = this.shadowRoot!.querySelector<HTMLElement>('[part=hint]')!; hint.textContent = this.hint; hint.hidden = !this.hint;
    const data = new this.ownerDocument.defaultView!.FormData(), state = new this.ownerDocument.defaultView!.FormData();
    for (const record of this._records) { if (this.name) data.append(this.name, record.file); state.append('files', record.file); }
    this._internals?.setFormValue(this.disabled || !this.name ? null : data, state);
    this._validate();
    const error = this.shadowRoot!.querySelector<HTMLElement>('[part=error]')!; error.textContent = this._message; error.hidden = !(this._showError || this.invalid) || this._validity.valid;
    this._chooser.setAttribute('aria-invalid', String(!error.hidden)); this.toggleAttribute('data-invalid', !error.hidden);
    const description = [hint.hidden ? '' : hint.id, error.hidden ? '' : error.id, `${this._id}-status`].filter(Boolean).join(' ');
    this._chooser.setAttribute('aria-describedby', description); this._group.setAttribute('aria-describedby', description);
    this._syncRows();
    const counts = new Map<string, number>();
    for (const task of this.tasks) counts.set(task.status, (counts.get(task.status) || 0) + 1);
    this.shadowRoot!.querySelector<HTMLElement>('[part=status]')!.textContent = `${this.readonly ? 'Read only. ' : ''}${this._records.length ? `${this._records.length} selected: ${[...counts].map(([state, count]) => `${count} ${state}`).join(', ')}.` : 'No files selected.'}`;
    const upload = this.shadowRoot!.querySelector<HTMLButtonElement>('[data-action=upload-all]')!;
    upload.hidden = !this._handler; upload.disabled = !this._mutable || !this._records.some(record => !record.rejection && ['queued', 'error', 'cancelled'].includes(record.status));
    const clear = this.shadowRoot!.querySelector<HTMLButtonElement>('[data-action=clear]')!; clear.disabled = !this._mutable || !this._records.length;
  }
  private _syncRows(): void {
    const active = this.shadowRoot!.activeElement as HTMLElement | null;
    for (const [id, row] of this._rows) if (!this._records.some(record => record.id === id)) { this._rows.delete(id); row.remove(); }
    for (const [index, record] of this._records.entries()) {
      let row = this._rows.get(record.id);
      if (!row) {
        row = this.ownerDocument.createElement('li'); row.id = record.id; row.setAttribute('part', 'file'); row.dataset.id = record.id;
        row.innerHTML = '<div class="file-info"><span class="filename"></span><p class="file-status"></p><progress part="progress" max="100"></progress></div><div class="file-actions"><button type="button" data-action="upload">Upload</button><button type="button" data-action="cancel">Cancel</button><button type="button" data-action="retry">Retry</button><button type="button" data-action="remove">Remove</button></div>';
        this._rows.set(record.id, row); this._list.append(row);
      }
      // Reorder only when needed; progress updates never replace/move focused nodes.
      if (this._list.children[index] !== row) this._list.insertBefore(row, this._list.children[index] || null);
      row.querySelector<HTMLElement>('.filename')!.textContent = record.file.name;
      const status = row.querySelector<HTMLElement>('.file-status')!; status.id = `${record.id}-status`;
      status.textContent = record.rejection || record.error || `${record.status}${record.progress === null ? '' : ` (${Math.round(record.progress)}%)`} · ${record.file.size} bytes`;
      const progress = row.querySelector('progress')!; progress.hidden = Boolean(record.rejection) || record.status !== 'uploading';
      progress.setAttribute('aria-label', `Upload progress for ${record.file.name}, file ${index + 1}`);
      if (record.progress === null) progress.removeAttribute('value'); else progress.value = record.progress;
      const actions: Record<string, boolean> = { upload: Boolean(this._handler) && record.status === 'queued' && !record.requested && !record.rejection, cancel: Boolean(record.controller || record.requested), retry: Boolean(this._handler) && !record.rejection && ['error', 'cancelled'].includes(record.status), remove: true };
      for (const button of row.querySelectorAll<HTMLButtonElement>('button')) {
        const action = button.dataset.action!; button.hidden = !actions[action]; button.disabled = !this._mutable;
        button.setAttribute('aria-label', `${button.textContent} ${record.file.name}, file ${index + 1}`); button.setAttribute('aria-describedby', status.id);
      }
    }
    if (active && (active.closest('[hidden]') || !active.isConnected) && !this.disabled && !this.readonly) this._chooser.focus();
  }
  private _onInvalid = (): void => { this._showError = true; this._sync(); };
  private _onEvent = (event: Event): void => {
    const target = event.target;
    if (event.type === 'input' && target === this._picker) event.stopPropagation();
    else if (event.type === 'change' && target === this._picker) {
      event.stopPropagation(); const files = [...this._picker.files || []]; this._picker.value = '';
      if (this._mutable && files.length) this._replace(this.multiple ? [...this.files, ...files] : files.slice(0, 1), true);
    } else if (event.type === 'click' && target instanceof HTMLElement) {
      const button = target.closest<HTMLButtonElement>('button[data-action]'); if (!button || button.disabled) return;
      const id = button.closest<HTMLElement>('[data-id]')?.dataset.id;
      switch (button.dataset.action) {
        case 'choose': this.choose(); break;
        case 'remove': this.removeFile(id!); break;
        case 'clear': this.clear(); break;
        case 'upload-all': void this.upload(); break;
        case 'upload': void this.upload(id); break;
        case 'retry': void this.retry(id!); break;
        case 'cancel': this.cancel(id); break;
      }
    } else if (event.type === 'dragover' || event.type === 'drop') {
      if (!this._mutable || !this.dropzone) return;
      event.preventDefault(); const drag = event as DragEvent;
      this._group.toggleAttribute('data-dragging', event.type === 'dragover');
      if (event.type === 'drop') { const files = [...drag.dataTransfer?.files || []]; if (files.length) this._replace(this.multiple ? [...this.files, ...files] : files.slice(0, 1), true); }
    } else if (event.type === 'dragleave') this._group.removeAttribute('data-dragging');
    else if (event.type === 'focusout') {
      const next = (event as FocusEvent).relatedTarget;
      if (next instanceof Node && this.shadowRoot!.contains(next)) return;
      // Removal can synchronously blur a row before the browser finishes
      // detaching it. Do not reconcile that same row inside its blur event.
      queueMicrotask(() => {
        if (!this.isConnected || this.shadowRoot!.activeElement) return;
        this._showError = true; this._sync();
      });
    }
  };
  private _render(): void {
    this.shadowRoot!.innerHTML = `<style>
      :host { display:block; max-width:100%; position:relative; font-family:var(--theme-font-family,'IBM Plex Sans',sans-serif); color:var(--theme-text-primary,#333); }
      input[type=file] { position:absolute; inset-block-start:0; inset-inline-start:0; width:1px; height:1px; padding:0; border:0; opacity:0; pointer-events:none; }
      [hidden] { display:none !important; }
      [part=group] { padding:1rem; border:1px solid var(--theme-border-secondary,#a0aabe); border-radius:var(--theme-radius-sm,4px); background:var(--theme-background-primary,white); }
      [part=group][data-dragging] { outline:2px solid var(--theme-accent-primary,#445e89); }
      [part=label] { display:block; margin:0 0 .5rem; font-weight:600; }
      button { min-width:44px; min-height:44px; padding:.5rem .75rem; border:1px solid var(--theme-border-secondary,#a0aabe); border-radius:var(--theme-radius-sm,4px); background:var(--theme-background-secondary,#f9fafb); color:var(--theme-text-primary,#333); font:inherit; cursor:pointer; }
      button:hover:not(:disabled) { background:var(--theme-background-hover,#eee); }
      button:focus-visible { outline:2px solid var(--theme-accent-primary,#445e89); outline-offset:2px; }
      button:disabled { opacity:.55; cursor:not-allowed; }
      .actions,.file-actions { display:flex; flex-wrap:wrap; gap:.5rem; }
      [part=dropzone],[part=hint],[part=status],.file-status { margin:.5rem 0; color:var(--theme-text-secondary,#555); font-size:.875rem; overflow-wrap:anywhere; }
      [part=files] { list-style:none; padding:0; margin:.75rem 0; }
      [part=file] { display:flex; flex-wrap:wrap; align-items:center; gap:.75rem; padding:.75rem 0; border-block-start:1px solid var(--theme-border-secondary,#a0aabe); }
      .file-info { flex:1 1 14rem; min-width:0; } .filename { overflow-wrap:anywhere; }
      progress { display:block; width:100%; accent-color:var(--theme-accent-primary,#445e89); }
      [part=error] { color:var(--theme-text-primary,#333); border-inline-start:3px solid var(--theme-error,#8b1f00); padding-inline-start:.5rem; font-size:.875rem; line-height:1.4; min-block-size:1.4em; margin:.5rem 0; }
      /* Keep the next form action in place when blur reveals validation. */
      [part=error][hidden] { display:block !important; visibility:hidden; }
      @media(forced-colors:active) { button,[part=group],[part=file] { border-color:ButtonText; } button:focus-visible { outline-color:Highlight; } }
    </style><div part="group" role="group"><span part="label"></span><p part="hint" id="${this._id}-hint"></p><p part="dropzone">Drop files here or use the chooser.</p><div class="actions"><button part="chooser" type="button" data-action="choose"></button><button type="button" data-action="upload-all">Upload files</button><button type="button" data-action="clear">Clear files</button></div><input type="file" tabindex="-1" aria-hidden="true"><ol part="files" aria-label="Selected files"></ol><p part="status" role="status" aria-live="polite" id="${this._id}-status"></p><p part="error" role="alert" id="${this._id}-error" hidden></p></div>`;
    this._chooser = this.shadowRoot!.querySelector('[part=chooser]')!; this._picker = this.shadowRoot!.querySelector('input')!;
    this._list = this.shadowRoot!.querySelector('[part=files]')!; this._group = this.shadowRoot!.querySelector('[part=group]')!;
  }
}
if (!customElements.get('qds-file-upload')) customElements.define('qds-file-upload', QdsFileUpload);
